import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { formatLift, formatTrialToPaid, type LiftTone } from '@/lib/template-stats';
import type { SalesSummary, StatsWindow, TrialToPaid } from '@/services/formTemplates';
import { InfoTip } from './InfoTip';
import { StatText } from './StatText';

const LIFT_TEXT_CLASSES: Record<LiftTone, string> = {
  up: 'text-emerald-600 dark:text-emerald-400',
  down: 'text-red-600 dark:text-red-400',
  flat: 'text-muted-foreground',
  none: 'text-muted-foreground',
};

function Figure({ label, value }: { label: string; value: TrialToPaid }) {
  const { rate, detail } = formatTrialToPaid(value);
  return (
    <div>
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className="text-2xl font-bold tracking-tight tabular-nums">
        <StatText value={rate} />
      </div>
      <div className="text-xs text-muted-foreground mt-0.5">{detail}</div>
    </div>
  );
}

interface SalesSummaryCardProps {
  summary: SalesSummary;
  days: StatsWindow;
  scope: 'library' | 'template';
}

export function SalesSummaryCard({ summary, days, scope }: SalesSummaryCardProps) {
  const lift = formatLift(summary.templateUsers, summary.allTrials);
  const imports = `${summary.imports} ${summary.imports === 1 ? 'import' : 'imports'}`;
  const agencies = `${summary.agencies} ${summary.agencies === 1 ? 'agency' : 'agencies'}`;

  return (
    <Card className="py-0 gap-0">
      <CardHeader className="px-5 py-4 gap-0 border-b">
        <CardTitle className="text-base font-semibold flex items-center gap-1.5">
          {scope === 'library' ? 'Library sales' : 'Template sales'}
          <InfoTip>
            {scope === 'library'
              ? `Trial to paid for agencies that used a template during their trial, next to all trials that started in the last ${days} days. An agency counts once its trial has ended.`
              : `Trial to paid for agencies that used this template during their trial, next to all trials that started in the last ${days} days. An agency counts once its trial has ended.`}
          </InfoTip>
        </CardTitle>
        <CardDescription>Trial to paid, template users next to all trials</CardDescription>
      </CardHeader>
      <CardContent className="p-5 space-y-4">
        <div className="flex flex-wrap items-start gap-x-10 gap-y-4">
          <Figure label="Template users" value={summary.templateUsers} />
          <Figure label="All trials" value={summary.allTrials} />
          <div className="flex items-center gap-2 sm:self-center">
            <span className={`text-sm font-medium ${LIFT_TEXT_CLASSES[lift.tone]}`}>{lift.text}</span>
            <span className="text-sm text-muted-foreground">vs all trials</span>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Not proof of cause: motivated users may pick templates more often. It tells us whether to invest more.
        </p>
        <p className="text-xs text-muted-foreground">
          {scope === 'library'
            ? `${imports} from ${agencies} in the last ${days} days. Staff imports aren't counted.`
            : `${imports} of this template from ${agencies} in the last ${days} days. Staff imports aren't counted.`}
        </p>
      </CardContent>
    </Card>
  );
}
